import React, { useMemo, useRef, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Typography, Dropdown } from "antd";
import { ArrowLeftOutlined, DownOutlined, FileExcelOutlined } from "@ant-design/icons";
import { SbTabs, SbTag, SbButton, SbModal, SbNotification, SbTooltip, SbCheckbox, SbSpin } from "uiCore";
import {
  PageContainerStyled,
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
} from "styles/common.styles";
import { PageTransitionStyled } from "./talentMobility.animations";
import { BackLinkStyled } from "./RoleDetailV2.styles";
import {
  SplitLayoutStyled,
  RightPanelStyled,
  ProfileSummaryColumnStyled,
  SkillsViewHeaderStyled,
  SkillsViewDropdownTriggerStyled,
  SkillsPaneStyled,
  SkillsMapScrollStyled,
} from "./CandidateProfileV2.styles";
import { ComingSoonStyled } from "Pages/SbJobDetailsV2/SbJobDetailsV2.styles";
import CandidateProfileCard from "./components/CandidateProfileCard";
import SkillGapList from "./components/SkillGapList";
import IndexTooltip, { IndexInfoLabel } from "./components/IndexTooltip";
import BubbleChart from "Components/SbBubbleChart/customBubbleChart";
import PlanSaveButton from "./components/PlanSaveButton";
// Reused as-is from SkillsBridger's own "Individual Profile Summary" panel
// (EmployeeSkillsMapV2) — same component, fed a mocked `aiSummary` object
// shaped exactly like the real ai_generated_summary.employee_summary
// response so a real API can slot in later without touching this component.
import EmployeeProfileCard from "Pages/SkillsBridgerV2/HrSkillsBridger/SkillsMap/components/EmployeeProfileCard";
import {
  useCandidateProfile,
  useEmployeeInsights,
  useSkillGapForPair,
  useSuccessorMatchForPair,
  useRoleReductionDetail,
  fetchNextRolesForPersonId,
} from "./useTalentMobilityData";
import {
  buildReductionCsvRows,
  computeReductionCutoff,
  orderReductionOccupants,
  parseReductionTarget,
} from "./RedeploymentReductionDetailV2/RedeploymentReductionDetailV2.utils";
import { exportRowsAsCsv } from "Utils/exportCsv.utils";
import { getScoreColor } from "Utils/scoreColor.utils";
import useTalentMobilitySettingsStore, { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import useShortlistStore, { useIsShortlisted } from "Store/useShortlistStore";
import useRedeploymentPlanStore, {
  useAvailableEmployeeIdsForRole,
  useShortlistReasonsForRole,
  useIsRoleTargetedForEmployee,
} from "Store/useRedeploymentPlanStore";
import useRedeploymentPlanActions from "./RedeploymentCenterV2/useRedeploymentPlanActions";
import ShortlistConfirmModal from "./components/ShortlistConfirmModal";
import useWorkforcePlanStore from "Store/useWorkforcePlanStore";
import useAvailabilityStore from "Store/useAvailabilityStore";
import { addTalentMobilityShortlist, removeTalentMobilityShortlist } from "Api/apiService";
import { ROLES } from "./mockData/talentMobility.mock";

import useAnalytics from "Hooks/useAnalytics";
import { ANALYTICS_EVENTS } from "Utils/analytics";
const { Title, Text } = Typography;

// Mirrors the view names SkillsBridger's employee-skills-map page offers in
// its header dropdown (SbSkillsMapV2.constants TEXT.DROPDOWN_*), so the two
// surfaces read the same. Here both render inline instead of navigating;
// Skills Proficiency (first entry) is the default view.
const SKILLS_VIEW_OPTIONS = [
  { key: "proficiency", label: "Skills Proficiency" },
  { key: "map", label: "Skills Map" },
];

// The match-score tag's size, split out so it reads larger than tag-default
// 12px without an inline literal.
const HEADLINE_TAG_STYLE = { fontSize: 13, padding: "5px 12px" };

const ANALYTICS_FROM = "talent_mobility_candidate_profile";
const ANALYTICS_EVENT = ANALYTICS_EVENTS.TALENT_MOBILITY.CANDIDATE_PROFILE_VIEWED;

const CandidateProfileV2 = () => {
  const track = useAnalytics(ANALYTICS_FROM);
  // This screen has no Mixpanel coverage of its own; the automatic
  // pageview only records the URL, not that the module was reached.
  useEffect(() => {
    track(ANALYTICS_EVENT);
  }, [track]);
  const { personId, roleId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  // This role's OPEN occupants, each with their own Open/Discoverable
  // snapshot — carried as router state from Role Detail's "View Detailed
  // Profile" link (see SkillGapAccordion.jsx), not the URL (it's a list of
  // objects). Absent on a direct/bookmarked visit, or when entered from any
  // flow other than Role Detail. Forwarded to POST /talent-mobility/shortlist
  // as `user_ids` in confirmShortlist below, so shortlisting a candidate also
  // captures this role's occupants' job-status without a separate checkbox
  // toggle.
  const openOccupants = location.state?.openOccupants ?? [];
  // Same underlying match score, labeled by the context this page was
  // entered from — Succession Planning/Role Detail pass ?index=ssi, every
  // other entry point (Redeployment, Career Pathways) defaults to SMI.
  const indexLabel = searchParams.get("index") === "ssi" ? "SSI" : "SMI";
  // Role occupant carried over from the Role Detail URL (?user_id=...) —
  // scopes the match read's teammate-exclusion (role-successor-analytics'
  // user_ids) the same way Role Detail's own successor list was scoped;
  // the shortlist write itself is role-scoped now (see confirmShortlist),
  // not keyed to this id.
  const roleOccupantUserId = searchParams.get("user_id");
  // Present only when entered from the redeployment flow (Employee View /
  // Reduction Detail) — the candidate's own current-role JD, keying which
  // redeployment plan a Shortlist here should append the viewed role to.
  const currentRoleJdId = searchParams.get("current_role");
  // Present only when entered from the Reduction Detail page's row panel —
  // its role title, carried over so this page can reproduce that page's
  // header (title + plan tag + description) instead of the default one.
  // Employee View's next-roles panel sets current_role too but never this,
  // so its entries keep the plain "Back" header.
  const currentRoleTitle = searchParams.get("current_role_title");
  const showReductionHeader = Boolean(currentRoleJdId && currentRoleTitle);
  // The reduction's at-risk cutoff, carried alongside current_role_title so
  // this page's replicated header/CSV rank occupants exactly as Reduction
  // Detail's own table does (see AtRiskDetailPanel's navigate call).
  const reductionTarget = parseReductionTarget(searchParams.get("target"));
  // Only fetch the reduction's full occupant list when actually reproducing
  // its header — Employee View's next-roles panel also sets current_role
  // (without current_role_title), and must not trigger this extra fetch.
  const reductionRoleId = showReductionHeader ? currentRoleJdId : undefined;
  const {
    role: reductionRole,
    occupants: reductionOccupants,
    isLoading: isReductionDataLoading,
  } = useRoleReductionDetail(reductionRoleId, currentRoleTitle);
  const availableEmployeeIdsForReduction = useAvailableEmployeeIdsForRole(reductionRoleId);
  const shortlistReasonsForReduction = useShortlistReasonsForRole(reductionRoleId);
  // Same ranking Reduction Detail's table uses, so "New Plan"/target-based
  // Status here matches what the CSV (and that page) would show.
  const orderedReductionOccupants = useMemo(
    () => orderReductionOccupants(reductionOccupants),
    [reductionOccupants]
  );
  const reductionCutoff = computeReductionCutoff(reductionRole, reductionTarget);
  // Save Plan / Save & Close Plan act on the same session-only plan draft
  // Reduction Detail's header buttons do — closing here lands back on the
  // Redeployment Center too, same as closing from there.
  const {
    hasDraft: hasReductionDraft,
    isSubmitting: isReductionPlanSubmitting,
    planName: reductionPlanName,
    planLabel: reductionPlanLabel,
    isUpdate: isReductionPlanUpdate,
    savePlan: saveReductionPlan,
  } = useRedeploymentPlanActions({ onClosed: () => navigate("/talent-mobility/redeployment-center") });
  const [isExportingReduction, setIsExportingReduction] = useState(false);
  // Same per-person next-roles cache Reduction Detail's own export keeps —
  // API occupants arrive with nextRoles: null until expanded, so a missing
  // one is fetched here too rather than exporting "—" for every row.
  const reductionExportCacheRef = useRef(new Map());

  const handleExportReductionCsv = async () => {
    if (!reductionRole) return;
    setIsExportingReduction(true);
    try {
      const cache = reductionExportCacheRef.current;
      const enrichedOccupants = await Promise.all(
        orderedReductionOccupants.map(async (occupant) => {
          if (occupant.person?.nextRoles) return occupant;
          const occupantPersonId = occupant.person?.id;
          if (!cache.has(occupantPersonId)) {
            cache.set(
              occupantPersonId,
              fetchNextRolesForPersonId(occupantPersonId).catch(() => {
                cache.delete(occupantPersonId);
                return null;
              })
            );
          }
          const nextRoles = await cache.get(occupantPersonId);
          return { ...occupant, person: { ...occupant.person, nextRoles } };
        })
      );
      const rows = buildReductionCsvRows({
        roleTitle: reductionRole.title,
        occupants: enrichedOccupants,
        cutoff: reductionCutoff,
        availableEmployeeIds: availableEmployeeIdsForReduction,
        shortlistReasons: shortlistReasonsForReduction,
      });
      exportRowsAsCsv(rows, `redeployment-reduction-${reductionRole.id}`);
    } finally {
      setIsExportingReduction(false);
    }
  };
  // Present only when entered from Workforce Planning's Candidates step —
  // a Shortlist here stays store-only (no POST /talent-mobility/shortlist)
  // and lands on the workforce plan draft under the viewed demand role,
  // submitted wholesale from that page's Submit button.
  const isWorkforceFlow = searchParams.get("flow") === "workforce";
  // Present only when entered from View Planning's read-only drill-down —
  // the plan is already saved, so the header carries no Shortlist or
  // availability action at all.
  const isViewPlanningFlow = searchParams.get("flow") === "view-planning";
  const { profile, isLoading } = useCandidateProfile(personId);
  // personId (not the loaded profile) keys the insights fetch, so
  // employee_summary fires in parallel with people-profile-details and
  // employee-skills instead of waiting for the profile response. Mock
  // profiles resolve synchronously, so their inline aiSummary still
  // short-circuits the fetch.
  const { insights, isLoading: isInsightsLoading } = useEmployeeInsights(personId, profile?.aiSummary);
  // employee_summary can also resolve with an object whose fields are all
  // empty — the card renders nothing for those, so treat them as no data too.
  const hasInsights = Boolean(insights && Object.values(insights).some(Boolean));
  const {
    skillGap,
    roleTitle: skillsRoleTitle,
    matchPct: skillsMatchPct,
    isLoading: isSkillGapLoading,
  } = useSkillGapForPair(roleId, personId);
  const {
    matchPct: successorMatchPct,
    apiShortlisted,
    isLoading: isSuccessorMatchLoading,
  } = useSuccessorMatchForPair(roleId, roleOccupantUserId, personId);
  const thresholds = useMatchThresholds();
  const role = ROLES.find((r) => r.id === roleId);
  // API pairs label the gap from /employee-skills (job_title + profile_score);
  // mock pairs keep the mock role title and the mock-computed match. The
  // successor-analytics match is the fallback for API pairs entered from Role
  // Detail before the skills read resolves.
  const roleTitle = role?.title || skillsRoleTitle;
  const matchPct = skillsMatchPct ?? successorMatchPct;
  const isStoreShortlisted = useIsShortlisted(roleId, personId);
  // Same source-of-truth rule as ShortlistTag.jsx: for API-backed pairs the
  // backend decides — a stale session-store entry must not show a
  // "Shortlisted" the analytics read disputes. justShortlisted covers the
  // gap between a successful POST on THIS visit and the next read. Draft
  // flows (mock role, redeployment, workforce) never POST until plan submit,
  // so the store stays their truth.
  const isApiBackedPair = !role && !currentRoleJdId && !isWorkforceFlow;
  const [justShortlisted, setJustShortlisted] = useState(false);
  // Mirrors justShortlisted for the opposite direction: masks the gap
  // between a successful DELETE on THIS visit and the next
  // /role-successor-analytics read, which would otherwise still report the
  // stale apiShortlisted=true until refetched.
  const [justUnshortlisted, setJustUnshortlisted] = useState(false);
  const isShortlisted = isApiBackedPair
    ? (apiShortlisted || justShortlisted) && !justUnshortlisted
    : isStoreShortlisted;
  const shortlistCandidate = useShortlistStore((state) => state.shortlistCandidate);
  const removeShortlist = useShortlistStore((state) => state.removeShortlist);
  const addEmployeeTargetRole = useRedeploymentPlanStore((state) => state.addEmployeeTargetRole);
  const addShortlistedEmployee = useWorkforcePlanStore((state) => state.addShortlistedEmployee);
  const removeShortlistedEmployee = useWorkforcePlanStore((state) => state.removeShortlistedEmployee);
  // Redeployment entries also get the Mark as Available / Unavailable toggle
  // in the header. It's scoped to the role being VIEWED, not the person:
  // marking available appends this roleId to targeted_jd_sb_unique_ids, so
  // the same person opened against a different next role starts back at
  // "Mark as Available". The person-level availability flag (Workforce
  // Planning's source) is set on the first mark and cleared only when the
  // last targeted role is removed.
  const isViewedRoleTargeted = useIsRoleTargetedForEmployee(currentRoleJdId, personId, roleId);
  const removeEmployeeTargetRole = useRedeploymentPlanStore((state) => state.removeEmployeeTargetRole);
  const decrementTargetPosition = useRedeploymentPlanStore((state) => state.decrementTargetPosition);
  const incrementTargetPosition = useRedeploymentPlanStore((state) => state.incrementTargetPosition);
  // Employee View's links carry ?origin=employee-view — its people-first
  // flow walks the current role's Target Positions down one per confirmed
  // mark. Profiles entered from the Reduction Detail (target-first) leave
  // the target where the user set it.
  const isEmployeeViewOrigin = searchParams.get("origin") === "employee-view";
  const markAvailable = useAvailabilityStore((state) => state.markAvailable);
  const markUnavailable = useAvailabilityStore((state) => state.markUnavailable);
  const notificationsEnabled = useTalentMobilitySettingsStore((state) => state.matchNotificationsEnabled);
  const [shortlistModalOpen, setShortlistModalOpen] = useState(false);
  const [undoShortlistModalOpen, setUndoShortlistModalOpen] = useState(false);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [isShortlisting, setIsShortlisting] = useState(false);
  const [isUndoingShortlist, setIsUndoingShortlist] = useState(false);
  const [skillsView, setSkillsView] = useState("proficiency");

  // POST /talent-mobility/shortlist keeps one shortlist row per JD — shared
  // by every occupant of the role, not scoped to roleOccupantUserId or any
  // other single user — so it's the same row /role-successor-analytics
  // reads its "shortlisted" flags from regardless of who's viewing. created_by
  // is not sent: the backend derives it from the JWT so it can't be spoofed.

  // "Skills Map" bubble chart — same component SkillsBridger's own detailed
  // profile page uses (Components/SbBubbleChart/customBubbleChart.jsx), but
  // scoped to the target role's skill gap (skillGap is already role vs
  // candidate) rather than the candidate's current role. `importance` has no
  // real backend source yet, so it's synthesized from required proficiency
  // scaled to the chart's expected 0-25 range.
  const bubbleData = useMemo(
    () =>
      skillGap.map((s) => ({
        label: s.skillName,
        requiredProficiency: s.required,
        currentProficiency: s.current,
        importance: s.required * 5,
      })),
    [skillGap]
  );

  const [notifyEmployee, setNotifyEmployee] = useState(false);
  const [notifyLineManager, setNotifyLineManager] = useState(false);
  const [notifyHiringManager, setNotifyHiringManager] = useState(false);

  const confirmShortlist = async () => {
    // Mock roles have no JD row behind them — only API-backed pairs persist
    // to POST /talent-mobility/shortlist; mock pairs stay store-only.
    // Redeployment-flow (?current_role=...) and workforce-flow
    // (?flow=workforce) entries skip the API too: their shortlist is part
    // of the flow's plan draft, submitted wholesale from that flow's Submit
    // button — not a per-JD shortlist row of its own.
    if (!role && !currentRoleJdId && !isWorkforceFlow) {
      setIsShortlisting(true);
      try {
        await addTalentMobilityShortlist({
          jd_sb_unique_id: roleId,
          shortlisted_user_id: personId,
          // This role's OPEN occupants' Open/Discoverable snapshot (see
          // openOccupants above) — already shaped as {user_id,
          // job_opening_status, is_job_discoverable} to match PUT
          // /job-opening-status's own field names, so it forwards straight
          // through. Omitted entirely when empty (a direct/bookmarked visit,
          // or any entry point other than Role Detail) rather than sending
          // an empty array for no reason.
          ...(openOccupants.length ? { user_ids: openOccupants } : {}),
          // Confirm dialog's checkboxes — the backend resolves each checked
          // audience (line manager via org_hierarchy, hiring manager via the
          // role's occupants) and sends "{name} will be shortlisted for
          // {job title}." through the notification stream.
          notify: {
            employee: notifyEmployee,
            line_manager: notifyLineManager,
            hiring_manager: notifyHiringManager,
          },
        });
      } catch (error) {
        SbNotification.error(
          "Unable to shortlist candidate",
          error?.response?.data?.message || "Please try again."
        );
        return;
      } finally {
        setIsShortlisting(false);
      }
    }
    setJustShortlisted(true);
    setJustUnshortlisted(false);
    shortlistCandidate(roleId, personId, notifyEmployee, notifyLineManager, notifyHiringManager);
    // Redeployment entry points append the shortlisted role to this
    // employee's targeted_jd_sb_unique_ids on their current role's plan,
    // carrying the dialog's notify choices onto the stored entry.
    if (currentRoleJdId)
      addEmployeeTargetRole(currentRoleJdId, personId, roleId, {
        employee: notifyEmployee,
        line_manager: notifyLineManager,
        hr_manager: notifyHiringManager,
      });
    // Workforce entry points record the employee under the viewed demand
    // role on the workforce plan draft, carrying the dialog's notify
    // choices onto the stored notifications map.
    if (isWorkforceFlow)
      addShortlistedEmployee(roleId, personId, {
        employee: notifyEmployee,
        line_manager: notifyLineManager,
        hr_manager: notifyHiringManager,
      });
    const silent = !notifyEmployee && !notifyLineManager && !notifyHiringManager;
    const notifiedWho = [
      notifyEmployee && "the employee",
      notifyLineManager && "their Line Manager",
      notifyHiringManager && "the Hiring Manager",
    ].filter(Boolean).join(" and ");
    SbNotification.success(
      "Candidate shortlisted",
      silent ? `${profile?.name} was shortlisted silently (no notification sent).` : `${profile?.name} was shortlisted — ${notifiedWho} notified.`
    );
    setShortlistModalOpen(false);
    setNotifyEmployee(false);
    setNotifyLineManager(false);
    setNotifyHiringManager(false);
  };

  // Undo Shortlist confirm. isApiBackedPair pairs have a real
  // global_talentmobility_plans row to remove personId from — the backend
  // re-notifies whichever audiences were notified when this person was
  // (last) shortlisted, read straight off that row's stored notifications
  // flags, so there are no notify checkboxes here. Store-only pairs (mock
  // roles, and workforce-flow entries — see confirmShortlist) never posted
  // anything, so undoing them is just dropping the session-store entry
  // (and, for workforce flow, this employee's slot on the draft).
  const confirmUndoShortlist = async () => {
    if (isApiBackedPair) {
      setIsUndoingShortlist(true);
      try {
        await removeTalentMobilityShortlist({
          jd_sb_unique_id: roleId,
          shortlisted_user_id: personId,
        });
      } catch (error) {
        SbNotification.error(
          "Unable to remove candidate from shortlist",
          error?.response?.data?.message || "Please try again."
        );
        return;
      } finally {
        setIsUndoingShortlist(false);
      }
      setJustShortlisted(false);
      setJustUnshortlisted(true);
    } else {
      removeShortlist(roleId, personId);
      if (isWorkforceFlow) removeShortlistedEmployee(roleId, personId);
    }
    SbNotification.success(
      "Removed from shortlist",
      `${profile?.name} was removed from the shortlist${roleTitle ? ` for ${roleTitle}` : ""}.`
    );
    setUndoShortlistModalOpen(false);
  };

  // Whether this person sits inside the target headcount of the reduction
  // this profile was opened from — same ranking/cutoff as Reduction Detail's
  // Status column. Always false outside that flow (no reduction header).
  const reductionRank = orderedReductionOccupants.findIndex((occupant) => occupant.person?.id === personId);
  const isRetainedInReduction = showReductionHeader && reductionRank > -1 && reductionRank < reductionCutoff;

  // The Recommend dialog asks for a Reason for Shortlisting when the profile
  // was opened from Employee View — same rule as Employee View's own
  // Shortlist — or for a Retained occupant opened from Reduction Detail,
  // matching that page's own Shortlist (which asks Retained rows for a
  // reason and At Risk rows not).
  const requireRecommendReason = isEmployeeViewOrigin || isRetainedInReduction;

  // Mark as Available confirm — records the availability (and the viewed
  // role on targeted_jd_sb_unique_ids) with the dialog's notify choices, plus
  // the Reason for Shortlisting when one was asked, so they land in the
  // plan's stored available_employee_targets JSON. is_retained is true only
  // for a Retained occupant opened from Reduction Detail; Employee View has
  // no reduction ranking to be "Retained" in.
  const confirmMarkAvailable = ({ notifications, reason }) => {
    // One seat per employee: decrement only on their FIRST mark for the
    // current role — a person already marked (from Employee View's panel, or
    // another next role's profile) doesn't free a second seat. Captured
    // before the mark lands on the store.
    const wasAlreadyAvailable = useRedeploymentPlanStore
      .getState()
      .isEmployeeAvailable(currentRoleJdId, personId);
    markAvailable(personId, "redeployment");
    // No shortlist context when no reason was asked, so a recommendation
    // never overwrites a reason the entry already carries with null.
    addEmployeeTargetRole(
      currentRoleJdId,
      personId,
      roleId,
      notifications,
      requireRecommendReason ? { isRetained: isRetainedInReduction, reason } : undefined
    );
    if (isEmployeeViewOrigin && !wasAlreadyAvailable) decrementTargetPosition(currentRoleJdId);
    setAvailabilityModalOpen(false);
  };

  // This employee's existing entry under the current role, if any — the
  // Recommend dialog pre-fills its notify choices from it.
  const existingPlanEntry = useRedeploymentPlanStore((state) =>
    state.availableEmployeeTargets[currentRoleJdId]?.find((entry) => entry.employee_unique_id === personId)
  );

  // "Recommend for this role" — skips the confirm dialog when this
  // employee's existing record under the current role (any earlier mark or
  // recommendation, even one a later revert emptied back out) already holds
  // everything the dialog would ask for: their notify choices, plus a Reason
  // for Shortlisting when one is required (requireRecommendReason). This
  // next role then just joins their targeted_jd_sb_unique_ids, reusing what's
  // saved. Otherwise — no record yet, or one missing a required reason (e.g.
  // a plan saved before reasons existed) — the dialog opens, pre-filled with
  // any saved notify choices.
  const handleRecommendClick = () => {
    const hasEverythingNeeded =
      Boolean(existingPlanEntry) && (!requireRecommendReason || Boolean(existingPlanEntry.shortlist_reason));
    if (hasEverythingNeeded) {
      markAvailable(personId, "redeployment");
      addEmployeeTargetRole(currentRoleJdId, personId, roleId);
    } else {
      setAvailabilityModalOpen(true);
    }
  };

  if (!profile && isLoading) {
    return (
      <PageContainerStyled>
        <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
          <SbSpin size="large" />
        </div>
      </PageContainerStyled>
    );
  }

  if (!profile) {
    return (
      <PageContainerStyled>
        <PageHeaderStyled>
          <HeaderLeftStyled>
            <Title level={4}>Candidate not found</Title>
          </HeaderLeftStyled>
        </PageHeaderStyled>
      </PageContainerStyled>
    );
  }

  const tabItems = [
    {
      key: "skills",
      label: "Skills",
      children: (
        <SkillsPaneStyled>
          <SkillsViewHeaderStyled>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {roleTitle && <Text strong>Skill Gap vs. {roleTitle}</Text>}
              {matchPct != null && (
                <IndexTooltip index={indexLabel}>
                  {/* Same enlarged-tag treatment as the Shortlisted tag in
                      the header — the match number is this page's headline
                      figure, so it can't sit at tag-default 12px. */}
                  <SbTag color={getScoreColor(matchPct, thresholds)} style={HEADLINE_TAG_STYLE}>
                    {matchPct}% {indexLabel}
                  </SbTag>
                </IndexTooltip>
              )}
            </div>
            <Dropdown
              trigger={["click"]}
              menu={{
                items: SKILLS_VIEW_OPTIONS,
                selectedKeys: [skillsView],
                onClick: ({ key }) => setSkillsView(key),
              }}
            >
              <SkillsViewDropdownTriggerStyled>
                {SKILLS_VIEW_OPTIONS.find((o) => o.key === skillsView)?.label} <DownOutlined />
              </SkillsViewDropdownTriggerStyled>
            </Dropdown>
          </SkillsViewHeaderStyled>
          <SbSpin spinning={isSkillGapLoading}>
            {/* Skills Proficiency = the gap list (current/required), same as
                SkillGapList's other consumers; Skills Map = the bubble chart. */}
            {skillsView === "map" ? (
              <SkillsMapScrollStyled>
                <BubbleChart skills={bubbleData} />
              </SkillsMapScrollStyled>
            ) : (
              // Bounds the row list to its own scrollbar so the legend and
              // column header stay pinned above it instead of scrolling
              // away with the rest of this (already tall) panel. fillHeight
              // (rather than a fixed pixel cap) makes that scroll box run to
              // the bottom of the page, so the Skills container gets the
              // same vertical space as the Individual Profile Summary card
              // beside it on every viewport.
              <SkillGapList skillGap={skillGap} fillHeight showScore={false} />
            )}
          </SbSpin>
        </SkillsPaneStyled>
      ),
    },
    {
      key: "completed-courses",
      label: "Completed Courses",
      children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
    },
    {
      key: "ongoing-courses",
      label: "Ongoing Courses",
      children: <ComingSoonStyled><Text type="secondary">Coming Soon..</Text></ComingSoonStyled>,
    },
  ];

  return (
    <PageTransitionStyled>
      {/* Entries from the Reduction Detail page's row panel (?current_role +
          ?current_role_title) get that page's own header — back link, role
          title + plan tag, description, and its Export/Save actions against
          the same plan draft — reproduced verbatim ahead of the profile's
          own header below, so the reduction's context stays visible while
          reviewing a candidate for it. */}
      {showReductionHeader && (
        <PageHeaderStyled>
          <HeaderLeftStyled>
            <BackLinkStyled onClick={() => navigate("/talent-mobility/redeployment-center")}>
              <ArrowLeftOutlined /> Back to Redeployment Center
            </BackLinkStyled>
            <HeadingRowStyled>
              <Title level={4}>{currentRoleTitle} — Reduction Detail</Title>
              <SbTag color={isReductionPlanUpdate ? "blue" : "gold"} style={{ fontSize: 14 }}>
                {reductionPlanLabel}
              </SbTag>
            </HeadingRowStyled>
            <Text type="secondary">
              Everyone currently in this role, ranked by <IndexInfoLabel index="SMI" />. Everyone below the target headcount is at risk in this reduction.
            </Text>
          </HeaderLeftStyled>
          <HeaderRightStyled>
            <SbButton
              buttonType="secondary"
              icon={<FileExcelOutlined />}
              loading={isExportingReduction}
              disabled={isReductionDataLoading}
              onClick={handleExportReductionCsv}
            >
              Export Redeployment Analysis CSV
            </SbButton>
            <PlanSaveButton
              planTypeLabel="Redeployment Plan"
              disabled={isReductionDataLoading}
              hasDraft={hasReductionDraft}
              isUpdate={isReductionPlanUpdate}
              planName={reductionPlanName}
              isSubmitting={isReductionPlanSubmitting}
              onSave={saveReductionPlan}
            />
            <PlanSaveButton
              planTypeLabel="Redeployment Plan"
              buttonType="secondary"
              closeAfter
              disabled={isReductionDataLoading}
              hasDraft={hasReductionDraft}
              isUpdate={isReductionPlanUpdate}
              planName={reductionPlanName}
              isSubmitting={isReductionPlanSubmitting}
              onSave={saveReductionPlan}
            />
          </HeaderRightStyled>
        </PageHeaderStyled>
      )}

      <PageHeaderStyled>
        <HeaderLeftStyled>
          {/* Own Back (browser history back) ahead of the candidate's name —
              distinct from the reduction header's Back above, which always
              goes to the Redeployment Center list. This one returns to
              whatever page — Reduction Detail included — was open before. */}
          <BackLinkStyled onClick={() => navigate(-1)}>
            <ArrowLeftOutlined /> Back
          </BackLinkStyled>
          <HeadingRowStyled>
            <Title level={4}>{profile.name}{profile.employeeId ? ` - ID ${profile.employeeId}` : ""}</Title>
          </HeadingRowStyled>
        </HeaderLeftStyled>
        <HeaderRightStyled>
          {/* View Planning entries (?flow=view-planning) are read-only —
              no Shortlist or availability action at all. Redeployment
              entries (?current_role=...) replace the whole Shortlist
              control with the availability toggle — marking available
              already records the viewed role on the plan's
              targeted_jd_sb_unique_ids, so a separate Shortlist action
              would be redundant there. */}
          {isViewPlanningFlow ? null : currentRoleJdId ? (
            isViewedRoleTargeted ? (
              <SbButton
                buttonType="secondary"
                onClick={() => {
                  removeEmployeeTargetRole(currentRoleJdId, personId, roleId);
                  // Clear the person-level flag only when that was their
                  // last targeted role — other roles keep them available.
                  if (!useRedeploymentPlanStore.getState().isEmployeeAvailable(currentRoleJdId, personId)) {
                    markUnavailable(personId);
                    // People-first flow: fully un-marking returns the seat
                    // the first mark took, so a mark/unmark round-trip
                    // leaves the target where it started.
                    if (isEmployeeViewOrigin) incrementTargetPosition(currentRoleJdId);
                  }
                }}
              >
                Revert recommendation
              </SbButton>
            ) : (
              // Disabled until the reduction ranking loads — whether a reason is
              // required depends on this person's Retained/At Risk status.
              <SbButton buttonType="primary" disabled={isReductionDataLoading} onClick={handleRecommendClick}>
                Recommend for this role
              </SbButton>
            )
          ) : isApiBackedPair && isSuccessorMatchLoading ? (
            // Whether this pair is already shortlisted only comes back from
            // /role-successor-analytics — without this, the page shows an
            // untruthful "Shortlist" button for a moment, then flips to
            // "Shortlisted" once that call resolves.
            <SbButton buttonType="primary" loading disabled>
              Shortlist
            </SbButton>
          ) : isShortlisted ? (
            <SbButton buttonType="secondary" onClick={() => setUndoShortlistModalOpen(true)}>
              Undo Shortlist
            </SbButton>
          ) : (
            <SbButton buttonType="primary" onClick={() => setShortlistModalOpen(true)}>Shortlist</SbButton>
          )}
        </HeaderRightStyled>
      </PageHeaderStyled>

      <SplitLayoutStyled>
        <RightPanelStyled>
          <SbTabs items={tabItems} />
        </RightPanelStyled>
        <ProfileSummaryColumnStyled>
          <CandidateProfileCard profile={profile} />
          <EmployeeProfileCard
            label="Individual Profile Summary"
            insights={insights}
            insightsLoading={isInsightsLoading}
            profileText={!hasInsights && !isInsightsLoading ? "No profile summary available." : ""}
            isEmpty={!hasInsights && !isInsightsLoading}
            keepScrollbarVisible
          />
        </ProfileSummaryColumnStyled>
      </SplitLayoutStyled>

      <SbModal
        open={shortlistModalOpen}
        title="Shortlist this candidate?"
        onCancel={() => setShortlistModalOpen(false)}
        footer={[
          <SbButton key="cancel" buttonType="secondary" onClick={() => setShortlistModalOpen(false)}>
            Cancel
          </SbButton>,
          <SbButton key="shortlist" buttonType="primary" loading={isShortlisting} onClick={confirmShortlist}>
            Shortlist
          </SbButton>,
        ]}
      >
        <Text>
          {profile.name} will be shortlisted{roleTitle ? ` for ${roleTitle}` : ""}.
        </Text>
        {/* One Shortlist action + two independent notify checkboxes (both
            default off = silent) — replaces the previous 4-button design
            (Silent / Notify Employee / Notify Line Manager / Notify Both),
            which was too many discrete options for one decision. */}
        <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 8 }}>
          <SbTooltip content={notificationsEnabled ? "" : "Match notifications are disabled in Admin Settings."}>
            <SbCheckbox
              checked={notifyEmployee}
              disabled={!notificationsEnabled}
              onChange={(e) => setNotifyEmployee(e.target.checked)}
            >
              Notify Employee
            </SbCheckbox>
          </SbTooltip>
          <SbCheckbox
            checked={notifyLineManager}
            onChange={(e) => setNotifyLineManager(e.target.checked)}
          >
            Notify Line Manager
          </SbCheckbox>
          <SbCheckbox
            checked={notifyHiringManager}
            onChange={(e) => setNotifyHiringManager(e.target.checked)}
          >
            Notify Hiring Manager
          </SbCheckbox>
        </div>
      </SbModal>

      <SbModal
        open={undoShortlistModalOpen}
        title="Remove from shortlist?"
        onCancel={() => setUndoShortlistModalOpen(false)}
        footer={[
          <SbButton key="cancel" buttonType="secondary" onClick={() => setUndoShortlistModalOpen(false)}>
            Cancel
          </SbButton>,
          <SbButton key="undo" buttonType="primary" loading={isUndoingShortlist} onClick={confirmUndoShortlist}>
            Undo Shortlist
          </SbButton>,
        ]}
      >
        <Text>
          {profile.name} will be removed from the shortlist{roleTitle ? ` for ${roleTitle}` : ""}.
          {isApiBackedPair
            ? " Anyone previously notified about this shortlist will be told they've been removed."
            : ""}
        </Text>
      </SbModal>

      {/* Mark as Available (redeployment entries) — the shared redeployment
          shortlist dialog: notify choices, plus the mandatory Reason for
          Shortlisting when opened from Employee View
          (requireRecommendReason), both stored on the plan entry. */}
      <ShortlistConfirmModal
        mode="recommend"
        requireReason={requireRecommendReason}
        open={availabilityModalOpen}
        personName={profile.name}
        roleTitle={roleTitle}
        initialValues={{ notifications: existingPlanEntry?.notifications }}
        onCancel={() => setAvailabilityModalOpen(false)}
        onConfirm={confirmMarkAvailable}
      />
    </PageTransitionStyled>
  );
};

export default CandidateProfileV2;
